import {
  Card,
  ConfirmationModal,
  Divider,
  MenuList,
  MenuListItem,
  ModalDialog,
  PrimaryButton,
  SecondaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'

import { Tabs } from '@webapp/core/components/Tabs'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import { type StatementItem } from '@webapp/modules/accounts-payable/entities/StatementItem'
import { CreateCreditCardItemFormPresenter } from '@webapp/modules/accounts-payable/features/create-credit-card-item/presenters/CreateCreditCardItemFormPresenter'
import { CreateStatementItemFormPresenter } from '@webapp/modules/accounts-payable/features/create-statement-item/presenters/CreateStatementItemFormPresenter'
import { EditCreditCardItemFormPresenter } from '@webapp/modules/accounts-payable/features/edit-credit-card-item/presenters/EditCreditCardItemFormPresenter'
import { EditReconcileSessionPresenter } from '@webapp/modules/accounts-payable/features/edit-reconcile-session/presenters/EditReconcileSessionPresenter'
import { InlineEditCreditCardItemPresenter } from '@webapp/modules/accounts-payable/features/inline-edit-credit-card-item/presenters/InlineEditCreditCardItemPresenter'
import { MatchedTransactionListPresenter } from '@webapp/modules/accounts-payable/features/list-matched-transactions/presenters/MatchedTransactionListPresenter'
import { UnmatchedCreditCardItemListPresenter } from '@webapp/modules/accounts-payable/features/list-unmatched-credit-card-items/presenters/UnmatchedCreditCardItemListPresenter'
import { UnmatchedStatementItemListPresenter } from '@webapp/modules/accounts-payable/features/list-unmatched-statement-items/presenters/UnmatchedStatementItemListPresenter'
import { StartReconciliationSessionFormPresenter } from '@webapp/modules/accounts-payable/features/start-reconciliation-session/presenters/StartReconciliationSessionFormPresenter'
import { MatchedStatementItemsList } from '@webapp/modules/accounts-payable/features/unmatch-match/presenters/UnmatchMatchPresenter'
import {
  MatchPageTabs,
  type MatchPageTabEnum,
} from '@webapp/modules/accounts-payable/pages/types'
import { AuditLogListPresenter } from '@webapp/platform/audit-log'
import { ReconciliationFooter } from './components/ReconciliationFooter'
import messages from './messages'
import { useReconciliationMatchView } from './useReconciliationMatchView'

export interface IReconciliationMatchViewProps {
  accountId: number
  tab: MatchPageTabEnum
}

/**
 * Feature Set: Reconciliation Match View (Page)
 *
 * Orchestrates the Unmatched/Matched tabs with count badges, the session
 * header controls, the two-pane matching workspace (selection bridges,
 * suggested-match cross-pane filter, inline-cost-cell + row-menu slots),
 * the FS-level sticky footer (selection variance / overall balances /
 * Submit gating), Submit navigation, and ALL modals: item create/edit/view,
 * statement item create, unmatch confirm, audit log, import statement and
 * the match-without-statement-items confirmation (ADR-9).
 */
export const ReconciliationMatchView = ({
  accountId,
  tab,
}: IReconciliationMatchViewProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const { state, methods } = useReconciliationMatchView({ accountId, tab })
  const { account, session } = state

  const currencyName = account?.currency.name ?? ''

  // Row-menu slots composing sibling command features (ADR-9).
  const renderCcRowMenu = (item: CreditCardItem) => (
    <MenuList
      ariaLabel={intl.formatMessage(messages.ccRowMenuAriaLabel, {
        description: item.descriptionLabel,
      })}
    >
      <MenuListItem
        icon='edit'
        text={intl.formatMessage(messages.editItemMenuItem)}
        onClick={() => methods.openEditItemModal(item)}
      />
      <MenuListItem
        icon='auditlog'
        text={intl.formatMessage(messages.viewAuditLogMenuItem)}
        onClick={() => methods.openAuditLogModal(item)}
      />
      {/* archive-credit-card-item hook-only command (BHV-051) */}
      <MenuListItem
        icon='delete'
        text={intl.formatMessage(messages.removeItemMenuItem)}
        disabled={!item.isEditable}
        onClick={() => methods.archiveCreditCardItem(item)}
      />
    </MenuList>
  )

  const renderStatementRowMenu = (item: StatementItem) => (
    <MenuList
      ariaLabel={intl.formatMessage(messages.statementRowMenuAriaLabel, {
        description: item.description,
      })}
    >
      {/* duplicate/remove-statement-item hook-only commands (BHV-042) */}
      <MenuListItem
        icon='duplicate'
        text={intl.formatMessage(messages.duplicateItemMenuItem)}
        onClick={() => methods.handleDuplicateStatementItem(item)}
      />
      <MenuListItem
        icon='delete'
        text={intl.formatMessage(messages.removeItemMenuItem)}
        onClick={() => methods.removeStatementItem(item.id)}
      />
    </MenuList>
  )

  const renderMatchedRowMenu = (
    item: CreditCardItem,
    match: ReconcileMatch
  ) => (
    <MenuList
      ariaLabel={intl.formatMessage(messages.ccRowMenuAriaLabel, {
        description: item.descriptionLabel,
      })}
    >
      {/* Matched items open read-only (BHV-019) */}
      <MenuListItem
        icon='edit'
        text={intl.formatMessage(messages.editItemMenuItem)}
        onClick={() => methods.openEditItemModal(item)}
      />
      <MenuListItem
        icon='delete'
        text={intl.formatMessage(messages.unmatchItemMenuItem)}
        onClick={() => methods.openUnmatchModal(match)}
      />
    </MenuList>
  )

  const renderInlineCostCell = (item: CreditCardItem) => (
    <InlineEditCreditCardItemPresenter
      item={item}
      onSaved={methods.handleInlineSaved}
    />
  )

  return (
    <>
      <Space>
        <Tabs>
          <Tabs.TabLink
            to={url('ReconciliationMatchPage', {
              id: String(accountId),
              tab: MatchPageTabs.UNMATCHED,
            })}
            count={state.unmatchedCount}
          >
            {intl.formatMessage(messages.unmatchedTabLabel)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('ReconciliationMatchPage', {
              id: String(accountId),
              tab: MatchPageTabs.MATCHED,
            })}
            count={state.matchedCount}
          >
            {intl.formatMessage(messages.matchedTabLabel)}
          </Tabs.TabLink>
        </Tabs>

        {session && (
          <Card>
            <Card.Content>
              <EditReconcileSessionPresenter
                session={session}
                currencyName={currencyName}
                lastStatementDate={account?.lastStatementDate ?? null}
                onSessionChange={methods.handleSessionChange}
              />
            </Card.Content>
          </Card>
        )}

        {state.activeTab === MatchPageTabs.UNMATCHED ? (
          <Space direction='horizontal' align='start'>
            <Space.Item width={3 / 5}>
              <Space>
                <Space direction='horizontal' justify='end'>
                  <SecondaryButton
                    type='button'
                    icon='add'
                    onClick={methods.openCreateItemModal}
                  >
                    {intl.formatMessage(messages.newItemButton)}
                  </SecondaryButton>
                </Space>
                <Card>
                  <Card.Content>
                    <UnmatchedCreditCardItemListPresenter
                      accountId={accountId}
                      cardCurrencyId={account?.currency.id ?? null}
                      selectedItemIds={state.selectedCcItems.map(
                        (item) => item.id
                      )}
                      onSelectionChange={methods.setSelectedCcItems}
                      onShowSuggestedMatches={methods.showSuggestedMatches}
                      renderInlineCostCell={renderInlineCostCell}
                      renderRowMenu={renderCcRowMenu}
                    />
                  </Card.Content>
                </Card>
              </Space>
            </Space.Item>

            <Space.Item width={2 / 5}>
              <Space>
                <Space direction='horizontal' justify='end' align='center'>
                  <PrimaryButton
                    type='button'
                    icon='add'
                    onClick={methods.openImportModal}
                  >
                    {intl.formatMessage(messages.importStatementButton)}
                  </PrimaryButton>
                  <SecondaryButton
                    type='button'
                    icon='add'
                    onClick={methods.openStatementItemModal}
                  >
                    {intl.formatMessage(messages.newItemButton)}
                  </SecondaryButton>
                </Space>
                <Card>
                  <Card.Content>
                    <UnmatchedStatementItemListPresenter
                      accountId={accountId}
                      filterByCcItemId={state.filterByCcItemId}
                      onClearFilter={methods.clearSuggestedFilter}
                      selectedItemIds={state.selectedStatementItems.map(
                        (item) => item.id
                      )}
                      onSelectionChange={methods.setSelectedStatementItems}
                      justCreatedItemIds={state.justCreatedItemIds}
                      renderRowMenu={renderStatementRowMenu}
                    />
                  </Card.Content>
                </Card>
              </Space>
            </Space.Item>
          </Space>
        ) : (
          <Card>
            <Card.Content>
              {session && (
                <MatchedTransactionListPresenter
                  sessionId={session.id}
                  currencyName={currencyName}
                  onBeginMatching={methods.goToUnmatchedTab}
                  renderRowMenu={renderMatchedRowMenu}
                />
              )}
            </Card.Content>
          </Card>
        )}
      </Space>

      <ReconciliationFooter
        currencyName={currencyName}
        hasSelection={state.footer.hasSelection}
        selectedCcCount={state.footer.selectedCcCount}
        selectedStatementCount={state.footer.selectedStatementCount}
        selectionTotal={state.footer.selectionTotal}
        selectionVariance={state.footer.selectionVariance}
        isMatchDisabled={state.footer.isMatchDisabled}
        isMatching={state.footer.isMatching}
        onDeselectAll={methods.clearSelections}
        onMatch={methods.handleMatch}
        endingBalance={state.footer.endingBalance}
        clearedBalance={state.footer.clearedBalance}
        overallVariance={state.footer.overallVariance}
        isSubmitDisabled={state.footer.isSubmitDisabled}
        onViewMatched={methods.goToMatchedTab}
        onSubmit={methods.handleSubmit}
      />

      <ModalDialog
        title={intl.formatMessage(messages.createItemModalTitle)}
        maxWidth='lg'
        open={state.itemModal?.mode === 'create'}
        onClose={methods.closeItemModal}
      >
        {state.itemModal?.mode === 'create' && account && (
          <CreateCreditCardItemFormPresenter
            account={account}
            onSuccess={methods.handleCcItemCreated}
            onCancel={methods.closeItemModal}
          />
        )}
      </ModalDialog>

      <ModalDialog
        title={intl.formatMessage(messages.editItemModalTitle)}
        maxWidth='lg'
        open={
          state.itemModal?.mode === 'edit' || state.itemModal?.mode === 'view'
        }
        onClose={methods.closeItemModal}
      >
        {(state.itemModal?.mode === 'edit' ||
          state.itemModal?.mode === 'view') &&
          state.itemModal.item && (
            <EditCreditCardItemFormPresenter
              item={state.itemModal.item}
              mode={state.itemModal.mode}
              onSuccess={methods.closeItemModal}
              onCancel={methods.closeItemModal}
            />
          )}
      </ModalDialog>

      <ModalDialog
        title={intl.formatMessage(messages.createStatementItemModalTitle)}
        maxWidth='sm'
        open={state.statementItemModalOpen}
        onClose={methods.closeStatementItemModal}
      >
        {state.statementItemModalOpen && (
          <CreateStatementItemFormPresenter
            accountId={accountId}
            onCreated={methods.handleStatementItemCreated}
            onCancel={methods.closeStatementItemModal}
          />
        )}
      </ModalDialog>

      {/* FS owns the unmatch confirmation modal + buttons (BHV-040) */}
      <ModalDialog
        title={intl.formatMessage(messages.unmatchModalTitle)}
        maxWidth='sm'
        open={state.unmatchModal !== null}
        onClose={methods.closeUnmatchModal}
      >
        {state.unmatchModal && (
          <Space>
            <MatchedStatementItemsList match={state.unmatchModal} />
            <Divider />
            <Space direction='horizontal' justify='end' align='center'>
              <TertiaryButton
                type='button'
                onClick={methods.closeUnmatchModal}
                disabled={state.isUnmatching}
              >
                {intl.formatMessage(messages.unmatchCancelButton)}
              </TertiaryButton>
              <PrimaryButton
                type='button'
                onClick={methods.handleUnmatchConfirm}
                disabled={state.isUnmatching}
              >
                {intl.formatMessage(messages.unmatchConfirmButton)}
              </PrimaryButton>
            </Space>
          </Space>
        )}
      </ModalDialog>

      {/* Existing platform/audit-log feature; content type resolved at
          runtime — never hardcoded (BHV-043) */}
      <ModalDialog
        title={intl.formatMessage(messages.auditLogModalTitle)}
        maxWidth='md'
        open={state.auditLogItem !== null}
        onClose={methods.closeAuditLogModal}
      >
        {state.auditLogItem && state.reconcileItemContentTypeId !== null && (
          <AuditLogListPresenter
            contentTypeId={state.reconcileItemContentTypeId}
            objectId={String(state.auditLogItem.id)}
          />
        )}
      </ModalDialog>

      {/* Import-only reconcile variant (BHV-029) — on close both panes
          refetch via the feature's invalidations */}
      <ModalDialog
        title={intl.formatMessage(messages.importStatementModalTitle)}
        maxWidth='md'
        open={state.importModalOpen}
        onClose={methods.closeImportModal}
      >
        {state.importModalOpen && account && (
          <StartReconciliationSessionFormPresenter
            account={account}
            importOnly
            onSuccess={methods.closeImportModal}
            onCancel={methods.closeImportModal}
          />
        )}
      </ModalDialog>

      {/* Match with statement items available but none selected (BHV-033) */}
      <ConfirmationModal
        open={state.matchConfirmOpen}
        type='warning'
        header={intl.formatMessage(messages.matchConfirmTitle)}
        content={intl.formatMessage(messages.matchConfirmContent)}
        confirmButtonText={intl.formatMessage(messages.matchConfirmButton)}
        cancelButtonText={intl.formatMessage(messages.matchConfirmCancelButton)}
        onConfirm={methods.handleMatchConfirm}
        handleDismiss={methods.dismissMatchConfirm}
      />
    </>
  )
}

ReconciliationMatchView.displayName = 'ReconciliationMatchView'
